import React from 'react';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import { Icon } from '../lumen';
import { Dropdown } from './Dropdown';

export const ProjectHeader = ({
  name,
  description,
  coverage,
  expanded = false,
  heliosLoading = false,
  heliosAlreadyAdded = false,
  onAddHeliosThemes,
  onAddData,
  onToggleExpand,
  onRename,
  onCopy,
  onDelete
}) => {
  const showKebab = Boolean(onRename || onCopy || onDelete);

  return (
    <Root>
      <TopRow>
        <Title title={name}>{name}</Title>
        <Actions>
          {onAddHeliosThemes && (
            <HeliosButton
              type="button"
              onClick={onAddHeliosThemes}
              disabled={heliosLoading || heliosAlreadyAdded}
              title={
                heliosAlreadyAdded
                  ? 'Helios themes have already been generated for this project.'
                  : undefined
              }
            >
              {heliosLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin style={ICON_STYLE} />
              ) : (
                <Icon name="sparkles" size={16} />
              )}
              <HeliosLabel>
                {heliosLoading
                  ? 'Adding Helios Themes…'
                  : heliosAlreadyAdded
                    ? 'Helios Themes Added'
                    : 'Add Helios Themes'}
              </HeliosLabel>
            </HeliosButton>
          )}
          {onAddData && (
            <PrimaryButton type="button" onClick={onAddData}>
              <Icon name="plus" size={16} />
              <span>Add Data</span>
            </PrimaryButton>
          )}
          {onToggleExpand && (
            <IconButton
              type="button"
              onClick={onToggleExpand}
              aria-label={expanded ? 'Expand' : 'Collapse'}
              title={expanded ? 'Expand' : 'Collapse'}
            >
              <Icon
                name={
                  expanded
                    ? 'arrow-up-right-and-arrow-down-left-from-center'
                    : 'arrow-down-left-and-arrow-up-right-to-center'
                }
                size={16}
              />
            </IconButton>
          )}
          {showKebab && (
            <Dropdown
              align="right"
              trigger={
                <IconButton
                  type="button"
                  aria-label="Project actions"
                  title="Project actions"
                >
                  <Icon name="ellipsis-stroke" size={16} />
                </IconButton>
              }
            >
              {onRename && (
                <MenuItem type="button" onClick={onRename}>
                  <Icon name="pen-line" size={14} />
                  <span>Rename</span>
                </MenuItem>
              )}
              {onCopy && (
                <MenuItem type="button" onClick={onCopy}>
                  <Icon name="clone" size={14} />
                  <span>Copy</span>
                </MenuItem>
              )}
              {onDelete && (
                <>
                  {(onRename || onCopy) && <MenuDivider />}
                  <MenuItem type="button" $danger onClick={onDelete}>
                    <Icon name="trash" size={14} />
                    <span>Delete</span>
                  </MenuItem>
                </>
              )}
            </Dropdown>
          )}
        </Actions>
      </TopRow>
      {!expanded && (description || coverage) && (
        <DescriptionRow>
          {description ? <Description>{description}</Description> : <span />}
          {coverage}
        </DescriptionRow>
      )}
    </Root>
  );
};

const ICON_STYLE = { width: 16, height: 16 };

/* Per Figma node 438:4061: gap 16px, padding 24px, radius 12px. No fill,
   no border — the header sits directly on the page. */
const Root = styled.header`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  padding: 1.5rem 0;
  border-radius: 12px;
  background: transparent;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
`;

const Title = styled.h1`
  margin: 0;
  padding: 0;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 32px;
  font-weight: 500;
  line-height: normal;
  color: ${({ theme }) => theme.color.fg.default};
  min-width: 0;
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Actions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
`;

const HeliosButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 32px;
  padding: 0 8px;
  background: ${({ theme }) => theme.color.bg.default};
  border: 1px solid #4f46e5;
  border-radius: 8px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 600;
  color: #4f46e5;

  &:hover:not(:disabled) {
    background: #eef2ff;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const HeliosLabel = styled.span`
  background: linear-gradient(to right, #4f46e5 0%, #2563eb 52%, #34d399 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 32px;
  padding: 0 8px;
  background: ${({ theme }) => theme.color.primary.base};
  border: 1px solid ${({ theme }) => theme.color.primary.base};
  border-radius: 8px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.primary.on};

  &:hover {
    background: ${({ theme }) => theme.color.primary.hover};
    border-color: ${({ theme }) => theme.color.primary.hover};
  }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: ${({ theme }) => theme.color.bg.default};
  border: 1px solid ${({ theme }) => theme.color.slate[300]};
  border-radius: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.color.fg.muted};

  &:hover {
    background: ${({ theme }) => theme.color.slate[100]};
    color: ${({ theme }) => theme.color.fg.default};
  }
`;

const DescriptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
`;

const Description = styled.p`
  margin: 0;
  padding: 0;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  color: ${({ theme }) => theme.color.fg.default};

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 16px;
  background: transparent;
  border: 0;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  color: ${({ $danger, theme }) =>
    $danger ? '#B91C1C' : theme.color.fg.default};

  &:hover {
    background: ${({ $danger, theme }) =>
      $danger ? theme.color.red[50] : theme.color.bg.subtle};
  }

  & > svg {
    color: ${({ $danger, theme }) =>
      $danger ? '#B91C1C' : theme.color.fg.subtle};
    flex-shrink: 0;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: #d9d9d9;
`;
